import { Card, Flex, Form, Input, Select, Switch, Typography } from "antd";
import {
	PLACEHOLDER_TYPES,
	type PlaceholderField,
	type PlaceholderValueType,
} from "@/common/placeholders";
import { AWS_ACCOUNT_ID_REGEX } from "@/components/environments/environment-base";
import { PlaceholderControl } from "./placeholder-input";

/** The form list the editor reads and writes; one entry per `{{key}}` in the text. */
export const PLACEHOLDERS_FIELD = "placeholders";

const cell = { flex: "1 1 220px", marginBottom: 0 };

/**
 * Configures a template's placeholders: one card per `{{key}}` token found in its
 * text, where the author sets the label, type, default and whether it's required.
 * The list itself is kept in step with the text by the owning form (see
 * `syncPlaceholders`), so a token is added here by typing it, not by a button.
 */
export function TemplatePlaceholdersEditor() {
	const form = Form.useFormInstance();
	const placeholders: PlaceholderField[] =
		// `preserve` returns the stored entries whole; without it only the subfields with
		// a mounted Form.Item come back, which drops `key`.
		Form.useWatch(PLACEHOLDERS_FIELD, { form, preserve: true }) ?? [];

	return (
		<Flex vertical gap={12}>
			<Typography.Paragraph style={{ marginBottom: 0 }}>
				Type a token such as{" "}
				<Typography.Text code>{"{{region}}"}</Typography.Text> into the name,
				description, instructions or verification criteria and it appears here.
				Whoever creates a task from this template is asked for each value, and
				it replaces the token in the task.
			</Typography.Paragraph>
			{placeholders.length === 0 && (
				<Typography.Text type="secondary" italic>
					This template has no placeholders.
				</Typography.Text>
			)}
			<Form.List name={PLACEHOLDERS_FIELD}>
				{(fields) =>
					fields.map(({ key, name }) => (
						<Card
							key={key}
							size="small"
							title={
								<Typography.Text
									code
								>{`{{${placeholders[name]?.key}}}`}</Typography.Text>
							}
						>
							<Flex wrap gap={12}>
								<Form.Item
									name={[name, "label"]}
									label="Label"
									rules={[{ required: true, whitespace: true, max: 200 }]}
									style={cell}
								>
									<Input maxLength={200} />
								</Form.Item>
								<Form.Item name={[name, "type"]} label="Type" style={cell}>
									<Select
										options={PLACEHOLDER_TYPES.map((t) => ({
											value: t.value,
											label: t.label,
											description: t.description,
										}))}
										optionRender={(option) => (
											<Flex vertical>
												<Typography.Text>{option.data.label}</Typography.Text>
												<Typography.Text
													type="secondary"
													style={{ fontSize: 12 }}
												>
													{option.data.description}
												</Typography.Text>
											</Flex>
										)}
										// A default of the old type rarely fits the new one.
										onChange={() =>
											form.setFieldValue(
												[PLACEHOLDERS_FIELD, name, "default"],
												undefined,
											)
										}
									/>
								</Form.Item>
								<Form.Item
									noStyle
									dependencies={[[PLACEHOLDERS_FIELD, name, "type"]]}
								>
									{() => {
										const type: PlaceholderValueType =
											form.getFieldValue([PLACEHOLDERS_FIELD, name, "type"]) ??
											"string";
										return (
											<Form.Item
												name={[name, "default"]}
												label="Default value"
												valuePropName={type === "boolean" ? "checked" : "value"}
												rules={
													type === "aws-account-id"
														? [
																{
																	pattern: AWS_ACCOUNT_ID_REGEX,
																	message: "Enter a 12-digit AWS account ID",
																},
															]
														: []
												}
												style={cell}
											>
												<PlaceholderControl type={type} />
											</Form.Item>
										);
									}}
								</Form.Item>
								<Form.Item
									name={[name, "required"]}
									label="Required"
									valuePropName="checked"
									style={cell}
								>
									<Switch size="small" />
								</Form.Item>
								<Form.Item
									name={[name, "description"]}
									label="Help text"
									rules={[{ max: 1000 }]}
									style={{ ...cell, flexBasis: "100%" }}
								>
									<Input
										maxLength={1000}
										placeholder="Shown under the input when the value is asked for"
									/>
								</Form.Item>
							</Flex>
						</Card>
					))
				}
			</Form.List>
		</Flex>
	);
}
